import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { FilterCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';
import { 
  Users, Search, RefreshCw, Edit2, Clock, Calendar, 
  Eye, Globe, Award, DollarSign, Building2, Video, TrendingUp, CheckCircle2, X 
} from 'lucide-react';

const HostManagement = () => {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);
  
  // Host Details Modal State
  const [isHostDetailsModalOpen, setIsHostDetailsModalOpen] = useState(false);
  const [selectedHostForView, setSelectedHostForView] = useState(null);
  const [hostDetailsData, setHostDetailsData] = useState(null);
  const [hostDetailsLoading, setHostDetailsLoading] = useState(false);

  // Country flags helper
  const countryFlags = {
    India: '🇮🇳',
    USA: '🇺🇸',
    'United States': '🇺🇸',
    UAE: '🇦🇪',
    'United Arab Emirates': '🇦🇪',
    Pakistan: '🇵🇰',
    Bangladesh: '🇧🇩',
    'Saudi Arabia': '🇸🇦',
    UK: '🇬🇧',
    'United Kingdom': '🇬🇧',
    Canada: '🇨🇦',
    Australia: '🇦🇺',
    Nepal: '🇳🇵',
    Kuwait: '🇰🇼',
    Qatar: '🇶🇦',
    Oman: '🇴🇲',
    Philippines: '🇵🇭',
    Indonesia: '🇮🇩'
  };
  const getFlag = (country) => countryFlags[country] || '🌐';

  const commonCountries = [
    "India", "United States", "United Arab Emirates", "Saudi Arabia", 
    "United Kingdom", "Pakistan", "Bangladesh", "Canada", "Australia", 
    "Nepal", "Kuwait", "Qatar", "Oman", "Philippines", "Indonesia"
  ];
  
  const [overrideForm, setOverrideForm] = useState({
    target_user_id: '',
    duration_mins: 120,
    valid_days: 10
  });

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type, visible: false }), 3500);
  };

  useEffect(() => {
    fetchHosts();
  }, []);

  const fetchHosts = async () => {
    setLoading(true);
    try {
      let res;
      try {
        res = await axios.get(`${constant.API_BASE_URL}/host/center/admin/hosts-list`, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });
      } catch (e) {
        if (e.response && e.response.status === 404) {
          // Fallback if production server hasn't restarted host_center routes yet
          res = await axios.get(`${constant.API_BASE_URL}/agencies/hosts`, {
            headers: { Authorization: sessionStorage.getItem('adminToken') }
          });
        } else {
          throw e;
        }
      }

      if (res && res.data) {
        const raw = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const agencyHostsOnly = (Array.isArray(raw) ? raw : []).filter(h => h.agency_name && h.agency_name.trim() !== '' && h.agency_name !== 'Independent');
        setHosts(agencyHostsOnly);
      }
    } catch (err) {
      console.error("Error fetching hosts:", err);
      showToast("Failed to fetch hosts: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOverride = (host = null) => {
    if (host) {
      setSelectedHost(host);
      setOverrideForm({
        target_user_id: host.xoxo_id || host.id,
        duration_mins: host.duration_mins > 0 ? host.duration_mins : 120,
        valid_days: host.valid_days > 0 ? host.valid_days : 10
      });
    } else {
      setSelectedHost(null);
      setOverrideForm({
        target_user_id: '',
        duration_mins: 120,
        valid_days: 10
      });
    }
    setIsModalOpen(true);
  };

  const handleOpenHostDetails = async (host) => {
    setSelectedHostForView(host);
    setIsHostDetailsModalOpen(true);
    setHostDetailsLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/host/center/admin/hosts/${host.id}/details`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data?.success) {
        setHostDetailsData(res.data.data);
      } else {
        throw new Error("Failed to load details");
      }
    } catch (err) {
      console.error("Error fetching host details:", err);
      // Fallback
      setHostDetailsData({
        host: host,
        targets: {
          current_level: host.current_level,
          achieved_coins: Number(host.achieved_coins || 0),
          host_salary_diamonds: Number(host.host_amount || 0),
          instant_salary: Number(host.valid_days || 0) >= 10
            ? Number(host.host_amount || 0)
            : Number(host.instant_amount || Math.floor(Number(host.host_amount || 0) * 0.7)),
          locked_salary: Number(host.valid_days || 0) >= 10
            ? 0
            : Number(host.locked_amount || Math.ceil(Number(host.host_amount || 0) * 0.3)),
          agency_commission_diamonds: Number(host.agency_comm_amount || 0),
          valid_days: host.valid_days || 0,
          duration_mins: host.duration_mins || 0,
          duration_hours: ((Number(host.duration_mins || 0)) / 60).toFixed(1),
          progress_percentage: Math.min(100, Math.round((Number(host.achieved_coins || 0) / 10000) * 100))
        },
        recent_sessions: []
      });
    } finally {
      setHostDetailsLoading(false);
    }
  };

  const handleOverrideSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/host/center/admin/override-active-stats`, {
        target_user_id: overrideForm.target_user_id,
        duration_mins: Number(overrideForm.duration_mins),
        valid_days: Number(overrideForm.valid_days)
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (res.data.success) {
        showToast(res.data.message || 'Host active stats updated successfully!');
        setIsModalOpen(false);
        fetchHosts();
      } else {
        showToast(res.data.message || 'Error updating stats', 'error');
      }
    } catch (err) {
      showToast("Error updating host stats: " + (err.response?.data?.message || err.message), 'error');
    }
  };

  const filteredHosts = hosts.filter(h => {
    if (!h.agency_name || h.agency_name === 'Independent') return false;
    const matchesSearch = 
      (h.xoxo_id && h.xoxo_id.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
      (h.first_name && h.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (h.last_name && h.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (h.email && h.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (h.agency_name && h.agency_name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCountry = countryFilter === 'ALL' || (h.country && h.country.toLowerCase() === countryFilter.toLowerCase());
    return matchesSearch && matchesCountry;
  });

  const totalHostSalary = hosts.reduce((acc, h) => acc + (Number(h.host_amount) || 0), 0);
  const totalAgencyComm = hosts.reduce((acc, h) => acc + (Number(h.agency_comm_amount) || 0), 0);
  const totalBDComm = hosts.reduce((acc, h) => acc + (Number(h.bd_comm_amount) || 0), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Host Management & Salary Calculations</h2>
          <p className="text-slate-500 mt-1">Monitor all agency-linked hosts, view live monthly progress, calculated salaries, commissions, and override active stats by XOXO ID.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={fetchHosts} 
            className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button 
            onClick={() => handleOpenOverride()} 
            className="flex items-center gap-2 bg-pink-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition"
          >
            <Edit2 size={18} /> Override Host Stats
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-pink-50 text-pink-600 flex items-center justify-center font-bold text-xl">💎</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Host Salary Pool</div>
            <div className="text-xl font-extrabold text-slate-800">💎 {totalHostSalary.toLocaleString()}</div>
            <div className="text-[11px] font-semibold text-slate-400">70% Instant / 30% Hold Split</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center font-bold text-xl">🏢</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Agency Comm Pool</div>
            <div className="text-xl font-extrabold text-slate-800">💎 {totalAgencyComm.toLocaleString()}</div>
            <div className="text-[11px] font-semibold text-slate-400">100% (10+ Days) / 70% (&lt;10 Days)</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl">💼</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">BD Comm Pool</div>
            <div className="text-xl font-extrabold text-slate-800">💎 {totalBDComm.toLocaleString()}</div>
            <div className="text-[11px] font-semibold text-slate-400">22% of Agency Payout</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-xl">👥</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Agency Hosts</div>
            <div className="text-xl font-extrabold text-slate-800">{hosts.length} Hosts</div>
            <div className="text-[11px] font-semibold text-slate-400">Linked to Agencies</div>
          </div>
        </div>
      </div>

      {/* Filter / Search Bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="w-full sm:w-60">
          <FilterCountrySelect
            value={countryFilter}
            onChange={setCountryFilter}
            placeholder="All Countries"
          />
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3 w-full flex-1">
          <Search className="text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search by XOXO ID, Name, Email, or Agency..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent border-none outline-none text-slate-700 placeholder-slate-400 text-sm font-medium"
          />
        </div>
      </div>

      {/* Hosts Table */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500">Loading Hosts Data...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Host / XOXO ID</th>
                  <th className="p-6">Agency</th>
                  <th className="p-6">Country</th>
                  <th className="p-6">Achieved Coins</th>
                  <th className="p-6">Target Level</th>
                  <th className="p-6">Calculated Host Salary</th>
                  <th className="p-6">Agency Commission</th>
                  <th className="p-6">BD Comm</th>
                  <th className="p-6">Active Days / Mins</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredHosts.map(host => {
                  const hostAmt = Number(host.host_amount || 0);
                  const validDays = Number(host.valid_days || 0);
                  const instantAmt = validDays >= 10 
                    ? hostAmt 
                    : Number(host.instant_amount !== undefined && host.instant_amount !== null ? host.instant_amount : Math.floor(hostAmt * 0.70));
                  const lockedAmt = validDays >= 10 
                    ? 0 
                    : Number(host.locked_amount !== undefined && host.locked_amount !== null ? host.locked_amount : (hostAmt - instantAmt));
                  const agencyAmt = Number(host.agency_comm_amount || 0);
                  const bdAmt = Number(host.bd_comm_amount || 0);

                  return (
                    <tr key={host.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-6">
                        <div className="flex items-center gap-3">
                          <img 
                            src={host.avatar_url || 'https://via.placeholder.com/150'} 
                            alt="Avatar" 
                            className="w-10 h-10 rounded-full object-cover border border-slate-200"
                          />
                          <div>
                            <div className="font-bold text-slate-800">{host.first_name || 'Host'} {host.last_name || ''}</div>
                            <div className="text-xs font-extrabold text-pink-600">XOXO ID: {host.xoxo_id || host.user_id || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="font-bold text-slate-800">{host.agency_name}</div>
                        {host.agency_code && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-100 mt-1">
                            Code: {host.agency_code}
                          </span>
                        )}
                      </td>
                      <td className="p-6">
                        <CountryBadge country={host.country} />
                      </td>
                      <td className="p-6 font-extrabold text-slate-700">🪙 {Number(host.achieved_coins).toLocaleString()}</td>
                      <td className="p-6">
                        <span className="px-3 py-1 bg-purple-50 text-purple-700 font-extrabold text-xs rounded-full border border-purple-100">
                          Level {Number(host.current_level || 0)}
                        </span>
                      </td>
                      <td className="p-6">
                        <div className="font-extrabold text-pink-600 text-base">💎 {hostAmt.toLocaleString()}</div>
                        <div className="text-[11px] font-semibold mt-0.5">
                          {validDays >= 10 ? (
                            <span className="text-emerald-600 font-bold">
                              Instant: 💎{instantAmt.toLocaleString()} • <span className="text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200">Hold: 💎0 (Unlocked)</span>
                            </span>
                          ) : (
                            <span className="text-slate-500">
                              <span className="text-emerald-600">Instant: 💎{instantAmt.toLocaleString()}</span> • <span className="text-amber-600 font-bold">Hold: 💎{lockedAmt.toLocaleString()}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="font-extrabold text-green-700 text-base">💎 {agencyAmt.toLocaleString()}</div>
                        <div className="mt-0.5">
                          {validDays >= 10 ? (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">100% Paid</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded">70% Paid (Penalty)</span>
                          )}
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="font-bold text-blue-600">💎 {bdAmt.toLocaleString()}</div>
                        <div className="text-[10px] font-medium text-slate-400">22% of Agency</div>
                      </td>
                      <td className="p-6">
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold ${validDays >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          <Calendar size={14} /> {validDays} / 10 Days
                        </div>
                        <div className="text-xs text-slate-500 font-medium mt-1 flex items-center gap-1">
                          <Clock size={12} className="text-blue-500" /> {host.duration_mins} mins
                        </div>
                      </td>
                      <td className="p-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => handleOpenHostDetails(host)} 
                            className="inline-flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-bold text-xs hover:opacity-95 transition shadow-sm whitespace-nowrap"
                          >
                            <Eye size={13} /> View Host
                          </button>
                          <button 
                            onClick={() => handleOpenOverride(host)} 
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold text-xs hover:opacity-95 transition shadow-sm whitespace-nowrap cursor-pointer"
                          >
                            <Clock size={13} /> Edit Timing & Days
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredHosts.length === 0 && (
                  <tr>
                    <td colSpan="10" className="p-10 text-center text-slate-500">No hosts found matching your criteria.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Host Timing & Days Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-indigo-50/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">
                    {selectedHost ? `Edit Timing & Days: ${selectedHost.first_name} ${selectedHost.last_name || ''}` : 'Edit Host Streaming Stats'}
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">Control active stream duration and monthly valid days</p>
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:bg-slate-100 p-2 rounded-xl transition cursor-pointer">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleOverrideSubmit} className="p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Host XOXO ID (Or User ID)</label>
                <input 
                  type="text" 
                  value={overrideForm.target_user_id} 
                  onChange={(e) => setOverrideForm({...overrideForm, target_user_id: e.target.value})}
                  placeholder="Enter XOXO ID (e.g. 100234)"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:outline-indigo-500 text-sm" 
                  required 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Valid Active Days</label>
                    <span className="text-[10px] font-bold text-indigo-600">{overrideForm.valid_days || 0} / 10 Target</span>
                  </div>
                  <input 
                    type="number" 
                    min="0"
                    max="31"
                    value={overrideForm.valid_days} 
                    onChange={(e) => setOverrideForm({...overrideForm, valid_days: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:outline-indigo-500 text-sm" 
                    required 
                  />
                  <span className="text-[10px] text-slate-400 font-medium block">1 Day = 120 mins stream</span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Total Duration (Mins)</label>
                    <span className="text-[10px] font-bold text-emerald-600">{((Number(overrideForm.duration_mins || 0)) / 60).toFixed(1)} Hrs</span>
                  </div>
                  <input 
                    type="number" 
                    min="0"
                    value={overrideForm.duration_mins} 
                    onChange={(e) => setOverrideForm({...overrideForm, duration_mins: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:outline-indigo-500 text-sm" 
                    required 
                  />
                  <span className="text-[10px] text-slate-400 font-medium block">Total minutes streamed</span>
                </div>
              </div>

              {Number(overrideForm.valid_days) >= 10 ? (
                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200/80 text-xs text-emerald-800 font-medium flex items-start gap-2.5">
                  <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-emerald-900 font-bold mb-0.5">10+ Valid Days Met (100% Full Payout)</strong>
                    Target basic salary (70%) + bonus (30%) will be fully unlocked. Any pending commissions for Agency, BD, and Manager will also be credited immediately!
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200/80 text-xs text-amber-800 font-medium flex items-start gap-2.5">
                  <Clock size={18} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-amber-900 font-bold mb-0.5">Under 10 Valid Days (70% Basic Salary Only)</strong>
                    Host receives 70% basic salary immediately upon target achievement. The remaining 30% bonus and commissions will stay in holding/pending until 10 days are completed.
                  </div>
                </div>
              )}

              <div className="pt-2 flex justify-end gap-3 border-t border-slate-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl font-bold text-sm transition shadow-lg shadow-indigo-500/25 cursor-pointer">
                  Save Timing & Days
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Host Details & Streaming History Modal */}
      {isHostDetailsModalOpen && selectedHostForView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full shadow-2xl border border-slate-100 animate-in zoom-in-95 my-8 max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-100 pb-5">
              <div className="flex items-center gap-4">
                <img
                  src={selectedHostForView.avatar_url || '/xoxo-logo.jpeg'}
                  alt="Host Avatar"
                  className="w-14 h-14 rounded-2xl object-cover border-2 border-pink-100 shadow-sm"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-black text-slate-900">
                      {selectedHostForView.first_name} {selectedHostForView.last_name}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-pink-50 text-pink-600 border border-pink-100">
                      Level {Number(hostDetailsData?.targets?.current_level ?? selectedHostForView.current_level ?? 0)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                    <span>XOXO ID: <strong className="text-slate-800">{selectedHostForView.xoxo_id || selectedHostForView.user_id || 'N/A'}</strong></span>
                    <span>•</span>
                    <span>Agency: <strong className="text-blue-600">{selectedHostForView.agency_name}</strong></span>
                    {selectedHostForView.agency_code && (
                      <span className="px-2 py-0.2 rounded bg-slate-100 font-bold text-slate-700">
                        Code: {selectedHostForView.agency_code}
                      </span>
                    )}
                    <span>•</span>
                    <span>{getFlag(selectedHostForView.country || 'India')} {selectedHostForView.country || 'India'}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsHostDetailsModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {hostDetailsLoading ? (
              <div className="py-20 text-center text-slate-400 font-medium">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-pink-600" />
                Loading detailed host streaming & target stats...
              </div>
            ) : (
              <div className="mt-5 space-y-6">

                {/* 4 Summary Stat Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Card 1: Level & Coins Target */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50/40 p-4 rounded-2xl border border-purple-100">
                    <div className="flex items-center justify-between text-xs font-bold text-purple-700 mb-1">
                      <span>TARGET LEVEL PROGRESSION</span>
                      <Award size={16} />
                    </div>
                    <div className="text-2xl font-black text-slate-900">
                      Level {Number(hostDetailsData?.targets?.current_level ?? selectedHostForView.current_level ?? 0)}
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1">
                      Achieved: <strong className="text-purple-700">🪙 {Number(hostDetailsData?.targets?.achieved_coins ?? selectedHostForView.achieved_coins ?? 0).toLocaleString()} Coins</strong>
                    </div>
                    {hostDetailsData?.targets?.next_level && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Next Level {hostDetailsData.targets.next_level.level_number}: {Number(hostDetailsData.targets.next_level.coins_remaining).toLocaleString()} coins remaining
                      </div>
                    )}
                  </div>

                  {/* Card 2: Calculated Diamonds Salary */}
                  <div className="bg-gradient-to-br from-pink-50 to-rose-50/40 p-4 rounded-2xl border border-pink-100">
                    <div className="flex items-center justify-between text-xs font-bold text-pink-700 mb-1">
                      <span>HOST CALCULATED SALARY</span>
                      <DollarSign size={16} />
                    </div>
                    <div className="text-2xl font-black text-pink-600">
                      💎 {Number(hostDetailsData?.targets?.host_salary_diamonds ?? selectedHostForView.host_amount ?? 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1 flex items-center gap-2">
                      {(Number(hostDetailsData?.targets?.valid_days ?? selectedHostForView?.valid_days ?? 0)) >= 10 ? (
                        <span className="text-emerald-600 font-bold">Instant (100%): 💎{Number(hostDetailsData?.targets?.host_salary_diamonds ?? selectedHostForView?.host_amount ?? 0).toLocaleString()} • Hold: 💎0 (Unlocked)</span>
                      ) : (
                        <>
                          <span className="text-emerald-600 font-bold">Instant (70%): 💎{Number(hostDetailsData?.targets?.instant_salary ?? selectedHostForView?.instant_amount ?? Math.floor(Number(selectedHostForView?.host_amount || 0) * 0.7)).toLocaleString()}</span>
                          <span>•</span>
                          <span className="text-amber-600 font-bold">Hold (30%): 💎{Number(hostDetailsData?.targets?.locked_salary ?? selectedHostForView?.locked_amount ?? Math.ceil(Number(selectedHostForView?.host_amount || 0) * 0.3)).toLocaleString()}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Card 3: Agency Commission */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50/40 p-4 rounded-2xl border border-blue-100">
                    <div className="flex items-center justify-between text-xs font-bold text-blue-700 mb-1">
                      <span>AGENCY COMMISSION</span>
                      <Building2 size={16} />
                    </div>
                    <div className="text-2xl font-black text-blue-600">
                      💎 {Number(hostDetailsData?.targets?.agency_commission_diamonds ?? selectedHostForView.agency_comm_amount ?? 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1">
                      Status: <strong className={Number(hostDetailsData?.targets?.valid_days ?? selectedHostForView.valid_days ?? 0) >= 10 ? 'text-emerald-600' : 'text-amber-600'}>
                        {Number(hostDetailsData?.targets?.valid_days ?? selectedHostForView.valid_days ?? 0) >= 10 ? '100% Full Commission' : '70% Commission (Under 10 Active Days)'}
                      </strong>
                    </div>
                  </div>

                  {/* Card 4: Streaming Activity */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50/40 p-4 rounded-2xl border border-emerald-100">
                    <div className="flex items-center justify-between text-xs font-bold text-emerald-700 mb-1">
                      <span>STREAMING ACTIVITY</span>
                      <Video size={16} />
                    </div>
                    <div className="text-2xl font-black text-emerald-700">
                      {hostDetailsData?.targets?.valid_days ?? selectedHostForView.valid_days ?? 0} <span className="text-sm font-bold text-slate-500">/ 10 Active Days</span>
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1">
                      Duration: <strong className="text-slate-800">{hostDetailsData?.targets?.duration_hours ?? ((Number(selectedHostForView.duration_mins || 0)) / 60).toFixed(1)} Hours</strong> ({hostDetailsData?.targets?.duration_mins ?? selectedHostForView.duration_mins ?? 0} Mins)
                    </div>
                    <button 
                      onClick={() => { setIsHostDetailsModalOpen(false); handleOpenOverride(selectedHostForView); }} 
                      className="mt-2.5 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-white/80 px-2.5 py-1 rounded-lg border border-emerald-200 cursor-pointer transition hover:bg-white"
                    >
                      <Clock size={12} /> Edit Timing & Days
                    </button>
                  </div>
                </div>

                {/* Level Progress Bar */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-2">
                    <span className="flex items-center gap-1.5">
                      <TrendingUp size={14} className="text-purple-600" />
                      Current Target Level Progression
                    </span>
                    <span className="text-purple-600 font-extrabold">
                      {hostDetailsData?.targets?.progress_percentage ?? 0}%
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, hostDetailsData?.targets?.progress_percentage ?? 0)}%` }}
                    />
                  </div>
                </div>

                {/* Streaming Sessions History */}
                <div>
                  <h4 className="text-sm font-black text-slate-900 mb-3 flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    Live Streaming Sessions History
                  </h4>

                  {(!hostDetailsData?.recent_sessions || hostDetailsData.recent_sessions.length === 0) ? (
                    <div className="bg-slate-50 rounded-2xl p-6 text-center text-xs font-medium text-slate-400 border border-dashed border-slate-200">
                      No live streaming sessions recorded for this host in the current period.
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                            <th className="p-3">Session Date</th>
                            <th className="p-3">Duration (Mins)</th>
                            <th className="p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {hostDetailsData.recent_sessions.map((sess, idx) => (
                            <tr key={sess.id || idx} className="hover:bg-slate-50/50">
                              <td className="p-3 font-semibold text-slate-700">
                                {sess.created_at ? new Date(sess.created_at).toLocaleString() : 'N/A'}
                              </td>
                              <td className="p-3 font-bold text-slate-800">
                                {sess.duration_mins} Mins
                              </td>
                              <td className="p-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${sess.is_valid_day ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {sess.is_valid_day ? <CheckCircle2 size={11} /> : null}
                                  {sess.is_valid_day ? 'Valid Active Day' : 'Sub-Threshold'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Footer button */}
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setIsHostDetailsModalOpen(false)}
                    className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-bold shadow-sm transition-colors"
                  >
                    Close
                  </button>
                </div>

              </div>
            )}

          </div>
        </div>
      )}

      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default HostManagement;
